import styles from './ClarityEngine.module.css';
import useRevealOnScroll from '../components/utils/js/useRevealOnScroll';
import { Link } from 'react-router-dom';

const ClarityEngine = () => {
  const addRef = useRevealOnScroll();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const weeks = [
    {
      week: 1,
      title: 'Foundations: How to Think',
      outcome: 'Turn any task into a step-by-step plan and track progress with simple tools'
    },
    {
      week: 2,
      title: 'Reading for Meaning',
      outcome: 'Understand structure, not just content, using active strategies (including AI prompts)'
    },
    {
      week: 3,
      title: 'Analytical Reasoning',
      outcome: 'Spot weak reasoning and missing steps with a systematic checklist'
    },
    {
      week: 4,
      title: 'Writing with Clarity',
      outcome: 'Write clear, structured paragraphs guided by a reusable template'
    },
    {
      week: 5,
      title: 'Problem Breakdown',
      outcome: 'Solve unfamiliar problems step-by-step with digital and analog aids'
    },
    {
      week: 6,
      title: 'AI as Thought Partner',
      outcome: 'Practice deeper responsible AI use for better idea generation and organization'
    },
    {
      week: 7,
      title: 'Apply to Real Work',
      outcome: 'Transfer skills to your own assignments across subjects'
    },
    {
      week: 8,
      title: 'Build Independence',
      outcome: 'Create your personal clarity system to use long-term'
    }
  ];

  const whoHelps = [
    'Students who struggle to organize their thinking across subjects',
    'Teens who want to understand what they’re reading, not just finish it',
    'Learners overwhelmed by assignments and unsure where to start',
    'Young people learning to use AI tools thoughtfully instead of relying on them',
    'Anyone seeking stronger academic independence rather than more tutoring'
  ];

  const learnsAbout = [
    'Structured reading and comprehension techniques',
    'Analytical reasoning framework',
    'Clear, organized writing habits',
    'Stepwise problem-solving strategies',
    'Using AI responsibly as a thinking and organizational tool',
    'Executive-function habits for planning and follow-through'
  ];

  return (
    <div className={styles.clarityPage}>
      {/* Hero Section */}
      <section className={styles.heroSection} ref={addRef(0.3)}>
        <h1 className={styles.mainHeadline}>The Clarity Engine</h1>
        <p className={styles.subheadline}>
          An 8‑week academic strategy program for high-school students — not tutoring, but a repeatable system for clearer reading, reasoning and writing, with AI integrated responsibly as a learning tool.
        </p>
        <p className={styles.supportLine}>
          Delivered by Dennis Marx, a certified teacher in British Columbia. Students build independent thinking skills they can apply across every subject.
        </p>
        <div className={styles.ctaRow}>
          <a href="mailto: dennis.marx93@gmail.com" className={styles.primaryButton}>
            Book Diagnostic Session
            </a>
          <button onClick={() => scrollToSection('program')} className={styles.secondaryButton} style={{ background: 'none', border: '2px solid #2148A1', color: '#2148A1', padding: '12px 32px', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem', fontWeight: '600', fontFamily: 'inherit' }}>
            How It Works
            </button>
        </div>
      </section>

      {/* Who This Helps */}
      <section className={styles.sectionWrapper} ref={addRef(0.4)}>
        <h2 className={styles.sectionHeading}>Who This Program Serves</h2>
        <ul className={styles.bulletList}>
          {whoHelps.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      </section>

      {/* What Students Learn */}
      <section className={styles.sectionWrapper} ref={addRef(0.4)}>
        <h2 className={styles.sectionHeading}>Skills & Habits Developed</h2>
        <div className={styles.learningGrid}>
          {learnsAbout.map((item, idx) => (
            <div key={idx} className={styles.learningCard}>
              <h3>{item}</h3>
            </div>
          ))}
        </div>
      </section>

      {/* 8-Week Program Overview */}
      <section id="program" className={styles.sectionWrapper} ref={addRef(0.4)}>
        <h2 className={styles.sectionHeading}>8-Week Academic Strategy Course</h2>
        <p className={styles.programDescription}>
          Each week introduces a structured thinking strategy with practice tasks and demonstrable skills. AI tools are integrated from week one, with deeper focus in week six. After every session, students get a specific tool or assignment to practice the skill in their regular classes. By the end, they have a personal clarity system for independent use.
        </p>
        <div className={styles.weeksGrid}>
          {weeks.map((w) => (
            <div key={w.week} className={styles.weekBox} ref={addRef(0.5)}>
              <span className={styles.weekNumber}>Week {w.week}</span>
              <h3 className={styles.weekBoxTitle}>{w.title}</h3>
              <p className={styles.weekOutcome}>{w.outcome}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className={styles.sectionWrapper} ref={addRef(0.4)}>
        <h2 className={styles.sectionHeading}>Program Details</h2>
        <div className={styles.pricingGrid}>
          <div className={styles.pricingCard}>
            <h3>Investment</h3>
            <p className={styles.price}>699 CAD</p>
            <p className={styles.priceNote}>per student</p>
          </div>
          <div className={styles.pricingCard}>
            <h3>Duration</h3>
            <p className={styles.price}>8 Weeks</p>
            <p className={styles.priceNote}>1-hour weekly sessions</p>
          </div>
          <div className={styles.pricingCard}>
            <h3>Cohort Size</h3>
            <p className={styles.price}>3–5 Students</p>
            <p className={styles.priceNote}>small group instruction</p>
          </div>
        </div>
      </section>

      {/* Start Dates */}
      <section className={styles.sectionWrapper} ref={addRef(0.4)}>
        <h2 className={styles.sectionHeading}>Start Your Journey</h2>
   
      </section>

      {/* Contact CTA */}
      <section id="contact" className={styles.contactSection} ref={addRef(0.4)}>
        <h2 className={styles.ctaHeading}>Ready to Build Academic Independence?</h2>
        <p className={styles.ctaText}>
          Join the next cohort, work with a qualified BC-certified teacher, and come away with skills you can apply in every class and long after the program ends.
        </p>
        <div className={styles.ctaButtons}>
          <a href='mailto:dennis.marx93@gmail.com' className={styles.primaryCta}>
            Get in Touch
          </a>
        </div>
      </section>
    </div>
  );
};

export default ClarityEngine;
